// Pure calculation helpers for Personal Zakat
// No React, no side effects - easy to unit test.

import { assetFields, nisabConstants, personTypes } from '../config/zakatConfig';

/**
 * Sum all asset values, converting gold/silver weight (grams) into currency.
 */
export const sumAssets = (assetValues, goldPricePerGram, silverPricePerGram) => {
  let total = 0;
  Object.entries(assetValues).forEach(([field, value]) => {
    const fieldConfig = assetFields[field];
    if (!fieldConfig) return;

    if (fieldConfig.type === 'weight') {
      if (fieldConfig.metalType === 'gold') {
        total += value * goldPricePerGram;
      } else if (fieldConfig.metalType === 'silver') {
        total += value * silverPricePerGram;
      }
    } else if (fieldConfig.type === 'currency') {
      total += value;
    }
  });
  return total;
};

/**
 * Sum all deduction values (plain currency).
 */
export const sumDeductions = (deductionValues) =>
  Object.values(deductionValues).reduce((sum, val) => sum + (val || 0), 0);

/**
 * Compute the nisab threshold in the active currency.
 */
export const computeNisab = (nisabStandard, goldPricePerGram, silverPricePerGram) => {
  if (nisabStandard === 'gold') {
    return nisabConstants.goldGrams * goldPricePerGram;
  }
  return nisabConstants.silverGrams * silverPricePerGram;
};

/**
 * Full personal Zakat calculation result.
 */
export const computeZakat = ({
  assetValues,
  deductionValues,
  nisabStandard,
  goldPricePerGram,
  silverPricePerGram,
  liveGoldPrice,
  liveSilverPrice,
  currency,
}) => {
  const totalAssets = sumAssets(assetValues, goldPricePerGram, silverPricePerGram);
  const totalDeductions = sumDeductions(deductionValues);
  const netWorth = totalAssets - totalDeductions;
  const nisab = computeNisab(nisabStandard, goldPricePerGram, silverPricePerGram);
  const isEligible = netWorth >= nisab;
  const zakatAmount = isEligible ? netWorth * nisabConstants.zakatRate : 0;

  return {
    totalAssets,
    totalDeductions,
    netWorth,
    nisab,
    isEligible,
    zakatAmount,
    zakatRate: nisabConstants.zakatRate * 100,
    goldPrice: goldPricePerGram,
    silverPrice: silverPricePerGram,
    liveGoldPrice,
    liveSilverPrice,
    currency,
    nisabStandard,
  };
};

/**
 * Available asset/deduction field keys for a person type.
 */
export const getPersonFields = (personId, kind = 'assetFields') => {
  if (!personId) return [];
  const config = personTypes.find((p) => p.id === personId);
  return config ? config[kind] : [];
};

/**
 * Merged & de-duplicated asset/deduction field keys for an array of person types.
 * Preserves the first-seen order so a stable rendering order results.
 */
export const getMergedPersonFields = (personIds, kind = 'assetFields') => {
  if (!Array.isArray(personIds) || personIds.length === 0) return [];
  const seen = new Set();
  const out = [];
  for (const id of personIds) {
    const fields = getPersonFields(id, kind);
    for (const f of fields) {
      if (!seen.has(f)) {
        seen.add(f);
        out.push(f);
      }
    }
  }
  return out;
};
